<template>
  <div>
    <!-- Show Device Configuration Dialog (TOTP, HOTP, Phone) -->
    <md-dialog :md-active.sync="showConfig" class="warning-dialog">
      <md-dialog-title>
        <md-icon>warning</md-icon>
        Config
      </md-dialog-title>
      <div class="md-dialog-content md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <div class="md-subheading warning-text">
          This config will be used for activate device. For security reasons, this is the last time the config is shown.
          It will not be accessible in the future. Please provide this for user.
        </div>
        <div class="config-url">
          <div v-if="selectedType === 'phone'" class="md-layout md-alignment-center">
            <vue-qrcode :value="`SamanActivationCode:` + configUrl" />
          </div>
          <div class="text-center" v-else-if="selectedType !== 'bypass_code'">
            <vue-qrcode :value="configUrl" />
          </div>
          <div>
            <small class="holder">{{ configUrl }}</small>
          </div>
          <md-button class="btn" @click="copyConfigToClipboard">
            <md-icon>file_copy</md-icon>
            Copy to clipboard
          </md-button>
        </div>
        <div dir="rtl">
          <md-button class="md-primary" @click="showConfig = false">OK</md-button>
        </div>
      </div>
    </md-dialog>

    <!-- Add New Device Dialog -->
    <md-dialog :md-active.sync="show" @md-clicked-outside="closeDialog" @md-closed="closeDialog">
      <md-dialog-title>Add new device</md-dialog-title>
      <div class="md-dialog-content md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <form>
          <md-field class="md-layout-item md-small-size-100 md-size-100">
            <label>Type</label>
            <md-select v-model="selectedType" name="type" id="type">
              <md-option value="phone">Phone</md-option>
              <md-option value="totp">TOTP</md-option>
              <md-option value="hotp">HOTP</md-option>
              <md-option value="sms">SMS</md-option>
              <md-option value="email">Email</md-option>
              <md-option value="hardware_totp">Hardware TOTP</md-option>
              <md-option value="bypass_code">Bypass Code</md-option>
              <md-option value="fido">FIDO Token</md-option>
            </md-select>
          </md-field>
          <md-field v-if="selectedType !== 'hardware_totp'" class="md-layout-item md-small-size-100 md-size-100">
            <label>Name</label>
            <md-input v-model="name"></md-input>
          </md-field>
          <md-field
            v-if="selectedType === 'sms' || selectedType === 'phone'"
            class="md-layout-item md-small-size-100 md-size-100"
            :class="phoneNumberClass"
          >
            <label>Phone Number</label>
            <md-input v-model="phoneNumber" required></md-input>
            <span class="md-error">{{ errors.phoneNumber }}</span>
          </md-field>
          <md-field
            v-if="selectedType === 'email'"
            class="md-layout-item md-small-size-100 md-size-100"
            :class="emailAddressClass"
          >
            <label>Email Address</label>
            <md-input v-model="emailAddress" required></md-input>
            <span class="md-error">{{ errors.emailAddress }}</span>
          </md-field>
          <md-field
            v-if="selectedType === 'bypass_code'"
            class="md-layout-item md-small-size-100 md-size-100"
            :class="validMinsClass"
          >
            <label>Valid Time (Minutes)</label>
            <md-input type="number" v-model="validMins"></md-input>
            <span class="md-error">{{ errors.validMins }}</span>
          </md-field>
          <md-field
            v-if="selectedType === 'bypass_code'"
            class="md-layout-item md-small-size-100 md-size-100"
            :class="reuseCountClass"
          >
            <label>Reuse Count</label>
            <md-input type="number" v-model="reuseCount"></md-input>
            <span class="md-error">{{ errors.reuseCount }}</span>
          </md-field>

          <template v-if="selectedType === 'hardware_totp'">
            <md-autocomplete
              v-if="!selectedHardwareTOTP.id"
              class="md-layout-item md-small-size-100 md-size-100"
              :class="hardwareTOTPClass"
              v-model="selectedHardwareTOTP"
              :md-options="availableHardwareTOTPDevices"
              @md-opened="searchAvailableHardwareTOTPDevices"
              @md-changed="searchAvailableHardwareTOTPDevices"
            >
              <label>Serial Number</label>
              <template slot="md-autocomplete-item" slot-scope="{ item }">{{ item.serialNumber }}</template>
              <template slot="md-autocomplete-empty" slot-scope="{ term }">
                <span class="text-breakable" v-if="term !== ''">
                  No hardware TOTP devices matching "{{ term }}" as serial number were found.
                </span>
                <span v-else class="text-breakable">
                  No hardware TOTP device is available, please add some before trying to devices them to users.
                </span>
              </template>
              <span class="md-error">{{ errors.devicePk }}</span>
            </md-autocomplete>

            <!-- Better showing of selected items -->
            <md-field v-else md-clearable @md-clear="selectedHardwareTOTP = ''" :class="hardwareTOTPClass">
              <label>Serial Number</label>
              <md-input v-model="selectedHardwareTOTP.serialNumber" readonly></md-input>
              <span class="md-error">{{ errors.devicePk }}</span>
            </md-field>
          </template>
        </form>
        <div class="md-layout-item md-size-100 text-right">
          <md-button @click="closeDialog">Close</md-button>
          <md-button class="md-primary" @click="createDevice">Add</md-button>
        </div>
      </div>
    </md-dialog>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import apiManager from "@/api-manager";
import _ from "lodash";
import VueQrcode from "vue-qrcode";
import NotificationStore from "../components/NotificationPlugin/NotificationStore";
import camelCaseObjectKeysToSnakeCase from "@/utils/camelCaseObjectKeysToSnakeCase";
import snakeCaseObjectKeysToCamelCase from "@/utils/snakeCaseObjectKeysToCamelCase";
import jwt_decode from "jwt-decode";

export default {
  name: "CreateNewDevice",
  props: ["application_user_id", "show"],
  components: {
    VueQrcode,
  },
  setup(props, { emit, root }) {
    const selectedType = ref("phone");
    const state = ref("create");

    const name = ref("");
    const phoneNumber = ref("");
    const emailAddress = ref("");
    const validMins = ref("");
    const reuseCount = ref("");

    const errors = ref({
      phoneNumber: "",
      emailAddress: "",
      devicePk: "", // For hardware TOTP
      validMins: "",
      reuseCount: "",
    });

    const phoneNumberClass = computed(() => {
      return { "md-invalid": errors.value.phoneNumber !== "" };
    });
    const emailAddressClass = computed(() => {
      return { "md-invalid": errors.value.emailAddress !== "" };
    });
    const validMinsClass = computed(() => {
      return { "md-invalid": errors.value.validMins !== "" };
    });
    const reuseCountClass = computed(() => {
      return { "md-invalid": errors.value.reuseCount !== "" };
    });

    const resetErrors = () => {
      errors.value = {
        phoneNumber: "",
        emailAddress: "",
        devicePk: "",
        validMins: "",
        reuseCount: "",
      };
    };

    const closeDialog = () => {
      resetErrors();
      phoneNumber.value = "";
      name.value = "";
      emailAddress.value = "";
      selectedHardwareTOTP.value = "";
      validMins.value = "";
      reuseCount.value = "";
      emit("close");
    };

    const showConfig = ref(false);
    const configUrl = ref("");
    const configBarcode = ref("");
    const copyConfigToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(configUrl.value);
        NotificationStore.notify({
          message: "Successfully copied!",
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
      } catch {
        /**/
      }
    };

    const createDevice = async () => {
      resetErrors();

      let data = {
        type: selectedType.value,
        application_user_pk: props.application_user_id,
      };

      switch (selectedType.value) {
        case "sms":
        case "phone":
          if (phoneNumber.value === "") {
            errors.value.phoneNumber = "Phone number is required";
            return;
          }
          data.phoneNumber = phoneNumber.value;
          break;
        case "email":
          if (emailAddress.value === "") {
            errors.value.emailAddress = "Email address is required";
            return;
          }
          data.emailAddress = emailAddress.value;
          break;
        case "hardware_totp":
          if (selectedHardwareTOTP.value === "" || !selectedHardwareTOTP.value.id) {
            errors.value.devicePk = "Serial number is required";
            return;
          }
          data.devicePk = selectedHardwareTOTP.value.id;
          break;
        case "bypass_code":
          if (validMins.value >= 1) {
            data.valid_mins = Number(validMins.value);
          } else {
            errors.value.validMins = "valid time should be greater than 0";
            return;
          }
          if (reuseCount.value >= 1) {
            data.reuseCount = Number(reuseCount.value);
          } else {
            errors.value.reuseCount = "reuse count should be greater than 0";
            return;
          }
          break;
      }

      let response;
      if (selectedType.value !== "hardware_totp") {
        data.name = name.value;

        response = await apiManager.createDevice({
          params: camelCaseObjectKeysToSnakeCase(data),
        });

        if (selectedType.value === "fido") {
          const base64 = require("js-base64");
          const config = jwt_decode(response.parsedData.config);
          if (config.registration_option) {
            try {
              const publicKeyCredential = await navigator.credentials.create({
                publicKey: {
                  ...config.registration_option,
                  challenge: base64.toUint8Array(config.registration_option.challenge),
                  user: {
                    ...config.registration_option.user,
                    id: base64.toUint8Array(config.registration_option.user.id),
                  },
                  excludeCredentials: config.registration_option.excludeCredentials.map((item) => ({
                    ...item,
                    id: base64.toUint8Array(item.id),
                  })),
                },
              });

              response = await apiManager.registerFidoToken({
                headers: {
                  Authorization: "FIDO-REG " + response.parsedData.config,
                },
                params: {
                  fido_response: publicKeyCredential.toFidoResponse(),
                },
              });
            } catch (error) {
              //  Errors are handled from the documents
              //  https://www.w3.org/TR/webauthn-2/#sctn-createCredential
              if (
                [
                  "AbortError",
                  "NotAllowedError",
                  "TypeError",
                  "OperationError",
                  "InvalidStateError",
                  "UnknownError",
                  "SecurityError",
                  "ConstraintError",
                  "NotSupportedError",
                ].includes(error.name)
              ) {
                NotificationStore.notify({
                  message: error.message,
                  icon: "error",
                  horizontalAlign: "right",
                  verticalAlign: "top",
                  type: "danger",
                });
              } else {
                throw error;
              }
              return;
            }
          }
        }
      } else {
        response = await apiManager.assignHardwareDevice({
          params: camelCaseObjectKeysToSnakeCase(data),
        });
      }

      if (response.status === 400) {
        console.debug(`Error while creating device: ${response.parsedError}`);

        errors.value[_.camelCase(response.parsedError)] = `Please enter a valid ${response.parsedError
          .toString()
          .replace(/device_pk/g, "serial_number")
          .replace(/_/g, " ")}`;
      }
      if (response.status === 201 || response.status === 200) {
        if (response.parsedData.config && selectedType.value !== "fido") {
          configUrl.value = response.parsedData.config;
          configBarcode.value = response.parsedData.config_barcode ? response.parsedData.config_barcode : "";
          showConfig.value = true;
        }
        if (selectedType.value === "fido") {
          NotificationStore.notify({
            message: response.parsedData.message,
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        }
        closeDialog();
      }
    };

    const selectedHardwareTOTP = ref("");
    const availableHardwareTOTPDevices = ref(Promise.resolve([]));
    const searchAvailableHardwareTOTPDevices = async (searchedSerialNumber) => {
      if (selectedType.value === "hardware_totp") {
        const response = await apiManager.getAvailableHardwareDevices({
          params: { application_user_pk: props.application_user_id },
        });

        if (response.status !== 200) {
          NotificationStore.notify({
            message: "Error while loading the list of available hardware TOTP devices.",
            icon: "error",
            horizontalAlign: "top",
            verticalAlign: "right",
            type: "error",
          });
          return;
        }

        const availableHardwareTOTPsResponse = snakeCaseObjectKeysToCamelCase(response.parsedData);
        availableHardwareTOTPDevices.value = new Promise((resolve) => {
          if (!searchedSerialNumber) resolve(availableHardwareTOTPsResponse);
          else {
            resolve(
              availableHardwareTOTPsResponse.filter((el) => {
                return el.serialNumber.includes(searchedSerialNumber);
              })
            );
          }
        });
      }
    };
    const hardwareTOTPClass = computed(() => {
      return { "md-invalid": errors.value.devicePk !== "" };
    });

    return {
      selectedType,
      state,

      // Fields
      name,
      emailAddress,
      phoneNumber,
      selectedHardwareTOTP,
      availableHardwareTOTPDevices,
      reuseCount,
      validMins,

      errors,

      // Config Window
      copyConfigToClipboard,
      configBarcode,
      configUrl,
      showConfig,

      // Computed Classes
      phoneNumberClass,
      emailAddressClass,
      hardwareTOTPClass,
      validMinsClass,
      reuseCountClass,

      closeDialog,
      createDevice,
      searchAvailableHardwareTOTPDevices,
    };
  },
};
</script>

<style lang="scss" scoped>
.config-url {
  margin: 16px;

  .btn {
    text-transform: none;
    margin-top: 8px;
    margin-left: 0;
    font-size: x-small;

    .md-icon {
      font-size: 16px !important;
    }
  }

  .holder {
    text-align: center;
    display: inline-block;
    width: 100%;
    margin: auto;
    border-style: solid;
    border-color: gray;
    border-width: 1px;
    background-color: gainsboro;
    border-radius: 4px;
    overflow-x: auto;
  }
}

.warning-text {
  margin: 16px;
}

span.text-breakable {
  display: block;
  width: 100%;
  max-width: 100%;
  white-space: normal;
  text-align: justify;
}
</style>
